import { useConsoleState } from "providers/ConsoleStateProvider";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  Interview,
  InterviewResource,
  InterviewType,
  Monitor,
  ResourceType,
} from "types/graphqlapi";
import { getChatDataQuery } from "utils/apis/chatData";
import { listInterviewsQuery } from "utils/apis/interview";
import { fetchInterviewResourcesBySubjectIdQuery } from "utils/apis/interviewResource";
import { fetchMonitorBysubjectIdQuery } from "utils/apis/monitor";
import {
  formatDateCompact,
  getDisplayMD,
  getDisplayYMDhm,
  getFileExtension,
  getStorageOutput,
} from "utils/common";

export type DisplayInterviewResource = {
  src?: string;
  interviewLabel: string;
  interview?: Interview;
  senderName?: string;
  senderMonitor?: Monitor;
  orderByAnsId?: number;
  typeDescription?: string;
  createdAtString?: string;
} & InterviewResource;

export const useInterviewResource = () => {
  const history = useHistory();
  const { selectedSubjectType } = useConsoleState();
  const [interviewResourceList, setInterviewResourceList] = React.useState<
    DisplayInterviewResource[]
  >([]);

  const fetchInterviewResource = async (subjectId: string) => {
    try {
      const interviewResourceList =
        await fetchInterviewResourcesBySubjectIdQuery(subjectId);

      const interviewList = await listInterviewsQuery(subjectId);

      const monitorList = await fetchMonitorBysubjectIdQuery(subjectId);

      const displayInterviewResourceList = await Promise.all(
        interviewResourceList.map(async (interviewResource) => {
          const src = await getStorageOutput(interviewResource.url);

          const targetInterview = interviewList.find(
            (interview) => interview.id === interviewResource.interview_id
          );
          const interviewLabel = `${targetInterview?.interview_seq} ${
            targetInterview?.scheduled_start_date
              ? getDisplayMD(new Date(targetInterview?.scheduled_start_date))
              : ""
          } ${targetInterview?.scheduled_start_time} ～ ${
            selectedSubjectType === InterviewType.TERM &&
            targetInterview?.scheduled_end_date
              ? getDisplayMD(new Date(targetInterview?.scheduled_end_date))
              : ""
          } ${targetInterview?.scheduled_end_time}`;

          const targetChatData = interviewResource.chat_data_id
            ? await getChatDataQuery(interviewResource.chat_data_id)
            : null;

          const senderMonitor = monitorList.find(
            (monitor) =>
              targetChatData?.monitor_id &&
              monitor.id === targetChatData?.monitor_id
          );

          const getSenderName = () => {
            if (senderMonitor) return senderMonitor.name || "モニター名なし";
            else if (targetChatData?.moderator_id) return "インタビュアー";
            else return "不明";
          };

          const senderName = getSenderName();

          const createdAtString = getDisplayYMDhm(
            new Date(interviewResource.createdAt)
          );

          const getTypeDescription = (fileType: ResourceType) => {
            switch (fileType) {
              case ResourceType.IMAGE:
                return "画像";
              case ResourceType.VIDEO:
                return "動画";
              default:
                return "その他";
            }
          };

          return {
            ...interviewResource,
            src,
            interviewLabel,
            interview: targetInterview,
            senderName,
            senderMonitor,
            createdAtString,
            typeDescription: getTypeDescription(interviewResource.type),
          };
        })
      );

      const sortedList = displayInterviewResourceList
        //インタビュアーがインタビュー中にアップロードしたファイルは表示しない
        .filter((resource) => resource.senderMonitor)
        .sort((a, b) => {
          // 実査開始日時
          const interviewStartDateTimeA =
            a.interview?.scheduled_start_date +
            " " +
            a.interview?.scheduled_start_time;
          const interviewStartDateTimeB =
            b.interview?.scheduled_start_date +
            " " +
            b.interview?.scheduled_start_time;

          // 実査終了日時
          const interviewEndDateTimeA =
            a.interview?.scheduled_end_date +
            " " +
            a.interview?.scheduled_end_time;
          const interviewEndDateTimeB =
            b.interview?.scheduled_end_date +
            " " +
            b.interview?.scheduled_end_time;

          // 実査No.
          const interviewSeqA = a.interview?.interview_seq;
          const interviewSeqB = b.interview?.interview_seq;

          // モニターのsort_index
          const monitorSortIndexA = a.senderMonitor?.sort_index;
          const monitorSortIndexB = b.senderMonitor?.sort_index;

          // 管理ID（AnsId）
          const monitorAnsIdA = a.senderMonitor?.ans_id;
          const monitorAnsIdB = b.senderMonitor?.ans_id;

          // 更新日時
          const createdAtA = a.createdAt;
          const createdAtB = b.createdAt;

          if (interviewStartDateTimeA !== interviewStartDateTimeB)
            return interviewStartDateTimeA > interviewStartDateTimeB ? 1 : -1;

          if (interviewEndDateTimeA !== interviewEndDateTimeB)
            return interviewEndDateTimeA > interviewEndDateTimeB ? 1 : -1;

          if (interviewSeqA && interviewSeqB && interviewSeqA !== interviewSeqB)
            return interviewSeqA > interviewSeqB ? 1 : -1;

          if (
            monitorSortIndexA &&
            monitorSortIndexB &&
            monitorSortIndexA !== monitorSortIndexB
          )
            return monitorSortIndexA > monitorSortIndexB ? 1 : -1;

          if (monitorAnsIdA && monitorAnsIdB && monitorAnsIdA !== monitorAnsIdB)
            return Number(monitorAnsIdA) > Number(monitorAnsIdB) ? 1 : -1;

          if (createdAtA !== createdAtB)
            return createdAtA > createdAtB ? 1 : -1;

          return a.id > a.id ? 1 : -1;
        });

      setInterviewResourceList(sortedList);
    } catch (error) {
      console.error(error);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    }
  };

  const getDownloadFileName = (interviewResource: DisplayInterviewResource) => {
    const interview = interviewResource.interview;
    const date = interview?.scheduled_start_date;
    const time = interview?.scheduled_start_time;

    if (!date || !time) return "";
    const interviewDate = new Date(`${date}T${time}`);
    const compactDateString = formatDateCompact(interviewDate);
    const fileExtention = getFileExtension(interviewResource.url);

    const fileName = `${interviewResource.senderMonitor?.ans_id}_${interviewResource.orderByAnsId}_${compactDateString}.${fileExtention}`;

    return fileName;
  };

  return {
    interviewResourceList,
    fetchInterviewResource,
    getDownloadFileName,
  };
};
